import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Clock, HandHeart } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTABand from "@/components/CTABand";
import SEO from "@/components/SEO";
import { businessSchema } from "@/data/schema";

const chapters = [
  {
    n: "01",
    title: "Mechanical Roots",
    body: "McCann and Sons began the way the best trades businesses do — a family, a truck, and a standard of craftsmanship that would not bend. HVAC work is mechanical work at its core: compressors, motors, refrigerant circuits, combustion systems, and controls all have to be diagnosed precisely and repaired correctly the first time. That mechanical discipline is the foundation everything else in our company is built on, and it is why our repairs hold.",
  },
  {
    n: "02",
    title: "Commercial Rigor",
    body: "Commercial mechanical HVAC is unforgiving. A failed rooftop unit means a hot dining room, an uncomfortable office, or product at risk — and every hour of downtime costs real money. We bring the same rigor to commercial clients that facility managers in larger markets expect: documented diagnostics, preventive maintenance agreements scheduled around your operating hours, priority emergency response, and clear communication from the first call to the final invoice.",
  },
  {
    n: "03",
    title: "The 24-Hour Commitment",
    body: "When we say 24/7, we mean a technician answers and a truck rolls — at 3 p.m. or 3 a.m. Central Louisiana summers are dangerous without cooling, and winter cold snaps put pipes, pets, and people at risk. Emergency readiness is not a marketing line for us; it is a staffing decision we make every single week, and it is the promise our name stands behind.",
  },
];

const values = [
  {
    icon: ShieldCheck,
    title: "Integrity",
    text: "We recommend what we would do in our own homes. If a repair buys you five more good years, we say so. If replacement is the honest answer, we show you exactly why — with numbers, not pressure.",
  },
  {
    icon: Clock,
    title: "Response Readiness",
    text: "Around-the-clock dispatch, stocked service vehicles, and honest arrival windows. When we give you a time, we keep it — and if anything changes, you hear it from us first.",
  },
  {
    icon: HandHeart,
    title: "Honest Recommendations",
    text: "Upfront flat-rate pricing before any wrench turns. No commission-driven upsells, no scare tactics, no fine print. Just a clear diagnosis and a fair price from a family that plans to serve you for decades.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Our Family HVAC Company | McCann & Sons"
        description="Family-owned HVAC contractor in Trout, LA serving Central Louisiana with mechanical craftsmanship, honest pricing & 24/7 response. Meet McCann & Sons."
        path="/about"
      />
      {/* PAGE HERO */}
      <section className="relative bg-navy-deep overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1667983453881-4992fe86ab1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGhlYXQlMjBwdW1wJTIwaG9tZSUyMHN5c3RlbXxlbnwwfHx8fDE3ODg0NTU2NzJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Industrial HVAC mechanical system serviced by McCann and Sons"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 to-navy-deep" />
          <div className="absolute inset-0 hero-grid-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-5">
            <MaskedLine delay={0.1}>Family-Owned · Trout, Louisiana</MaskedLine>
          </p>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.92] text-white">
            <MaskedLine delay={0.2}>Craftsmanship</MaskedLine>
            <MaskedLine delay={0.35}>
              You Can <span className="text-ember">Call At 2 A.M.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <Marquee items={["Our Mission", "Integrity", "24-Hour Readiness", "Honest Recommendations", "Residential", "Commercial", "Central Louisiana"]} dark />

      {/* MISSION */}
      <section className="bg-white py-20 lg:py-32" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Our Mission</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Reliable Heating & Cooling, Built Like Family
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>
              Our mission is simple to state and hard to fake: deliver mechanical craftsmanship and
              reliable heating and cooling solutions to every home and business we serve in Central
              Louisiana — at a fair price, on an honest timeline, with a technician who treats your
              property like their own.
            </p>
            <p>
              Being family-owned shapes every decision we make. There is no distant corporate office
              setting sales quotas, and no incentive to sell equipment you do not need. When a McCann and
              Sons technician hands you a recommendation, it carries our family name — and in a region the
              size of LaSalle Parish, a name is the most valuable asset a company has.
            </p>
            <p>
              From our shop at 164 Dixie Lane in Trout, we have built our reputation one repaired system,
              one honest quote, and one answered midnight call at a time. That is the business our sons
              will inherit, and we run it that way every day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="bg-navy-deep py-20 lg:py-32 relative overflow-hidden grain-overlay" data-testid="manifesto-section">
        <div className="hero-grid-bg absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-4">The McCann Manifesto</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              Three Chapters. One Standard.
            </h2>
          </Reveal>
          <div className="space-y-0 border-t border-white/10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 py-12 lg:py-16 border-b border-white/10">
                  <div className="lg:col-span-3">
                    <span className="font-display text-7xl lg:text-8xl font-black text-white/10">{c.n}</span>
                  </div>
                  <div className="lg:col-span-4">
                    <h3 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-wide text-white">{c.title}</h3>
                  </div>
                  <div className="lg:col-span-5">
                    <p className="text-slate-300 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RESIDENTIAL & COMMERCIAL FOCUS */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="dual-focus-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Two Worlds, One Crew</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Heavy-Duty Commercial Systems & Household Comfort
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full bg-white border-t-4 border-t-frost border-x border-b border-slate-200 p-8 lg:p-10 rounded-sm">
                <h3 className="font-display text-3xl font-bold uppercase tracking-wide text-navy">Residential Expertise</h3>
                <p className="mt-5 text-slate-600 leading-relaxed">
                  Your home's system has one job: keep your family comfortable without you ever thinking
                  about it. We service every type of residential equipment common in Central Louisiana —
                  central air conditioners, gas and electric furnaces, heat pumps, and ductless mini-splits.
                  Whether it is a midnight breakdown, a seasonal tune-up, or a full high-efficiency
                  replacement sized correctly for your home's actual load, we bring the same care we would
                  want in our own living rooms.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full bg-white border-t-4 border-t-ember border-x border-b border-slate-200 p-8 lg:p-10 rounded-sm">
                <h3 className="font-display text-3xl font-bold uppercase tracking-wide text-navy">Commercial Mechanical Depth</h3>
                <p className="mt-5 text-slate-600 leading-relaxed">
                  Commercial mechanical HVAC demands a different level of discipline: rooftop package units,
                  multi-zone split systems, ventilation, and equipment that runs hard for twelve or more
                  hours a day. We manage heavy-duty systems for offices, retail spaces, churches,
                  restaurants, and industrial facilities — with maintenance agreements that prevent
                  downtime and 24/7 emergency coverage that contains it when prevention is not enough.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-white py-20 lg:py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost-dark mb-4">Core Values</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              What We Will Not Compromise
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full border border-slate-200 rounded-sm p-8 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(15,9,71,0.1)] transition-all duration-300">
                  <v.icon className="h-7 w-7 text-ember mb-5" />
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-navy">{v.title}</h3>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 flex flex-col sm:flex-row gap-4">
            <a
              data-testid="about-call-btn"
              href="tel:3183122112"
              className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-dark text-navy-deep font-bold px-8 py-4 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
            >
              <Phone className="h-4 w-4" />
              Call (318) 312-2112
            </a>
            <Link
              data-testid="about-services-btn"
              to="/services"
              className="inline-flex items-center justify-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white font-bold px-8 py-4 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
            >
              Explore Our Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand title="Talk To A Real Local Technician" sub="No call centers, no scripts. Reach the McCann and Sons team directly — 24 hours a day, 7 days a week." />
    </div>
  );
}
